import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import vdlPrefix from '../../util/vdlPrefix';
import { ITabOrientation, Tab } from './tab';
import { contains, dataId, dataIndexOf, dataText, dataValue, generateId, isDisabledItem, message as messageLocalizer, notify, result, valueMatcher } from '@synerg/vdl-react-components';

const optionId = (id, idx) => `${id}__tab__${idx}`;

const tabContentId = (id, idx) => `${id}__tabs__content__${idx}`;

function localizedMessage(messageKey: string, locale?: string) {
  return messageLocalizer.formatMessage('adp-react-components/tabs/' + messageKey, undefined, locale);
}

function msgs(tabsMsgs?: ITabsMessages, locale?: string) {
  return Object.assign(
    {
      emptyTabs: localizedMessage('EMPTY_TABS', locale)
    },
    tabsMsgs
  );
}

export type ITabValueAccessor = (item: any) => string;

export interface ITabsMessages {
  emptyTabs?: React.ReactNode;
}

export interface ITabsProps {
  children?: React.ReactNode;
  className?: string;
  data: any[];
  disabled?: boolean;
  disabledItems?: any[];
  // Keyboard accessibility: if true, this property removes the built-in tab stop inside the tabpanel.
  // This built-in tab stop is needed to enter the tabpanel, if no interactive elements are available inside it.
  // If true, the tabpanel is expected to have an interactive element available in the tabbing order.
  disableTabPanelTabStop?: boolean;
  idField?: string | ITabValueAccessor;
  itemComponent?: React.ClassType<any, any, any>;
  lastTabComponent?: React.ClassType<any, any, any>;
  messages?: ITabsMessages;
  orientation?: ITabOrientation;
  role?: string;
  tabComponent?: React.ClassType<any, any, any>;
  textField?: string | ITabValueAccessor;
  value?: any;
  valueField?: string | ITabValueAccessor;
  onChange?: (value: any) => void;
}

export const Tabs = ({
  children,
  data = [],
  disabled = false,
  disableTabPanelTabStop,
  idField,
  itemComponent: ItemComponent,
  lastTabComponent: LastTabComponent,
  orientation = 'horizontal',
  role = 'tablist',
  tabComponent: TabComponent = Tab,
  textField,
  value,
  valueField,
  ...props
}: ITabsProps) => {
  const initialIdx = dataIndexOf(data, value, valueField);
  const messages = msgs(props.messages);
  const id = generateId(props);
  const [selectedItem, setSelectedItem] = useState(data[initialIdx] || data[0]);

  useEffect(() => {
    const idx = dataIndexOf(data, value, valueField);
    setSelectedItem(data[idx]);
  });

  const onChange = (item: any) => {
    if (!valueMatcher(item, value, valueField)) {
      notify(props.onChange, dataValue(item, valueField));
      setSelectedItem(item);
    }
  };

  const onNavigate = (direction: string) => {
    const mappedSteps = data.map((item) => {
      if (props.disabledItems?.includes(dataValue(item, valueField))) {
        return undefined;
      }
      else {
        return item;
      }
    });

    const currentIndex = dataIndexOf(data, value, valueField);

    switch (direction) {
      case 'previous': {
        let newIndex = currentIndex;

        while (newIndex > 0) {
          if (mappedSteps[newIndex - 1]) {
            newIndex = newIndex - 1;
            notify(props.onChange, dataValue(data[newIndex], valueField));
            setSelectedItem(data[newIndex]);
            return;
          }
          else {
            newIndex = newIndex - 1;
          }
        }

        break;
      }

      case 'next': {
        let newIndex = currentIndex;

        while (newIndex < mappedSteps.length) {
          if (mappedSteps[newIndex + 1]) {
            newIndex = newIndex + 1;
            notify(props.onChange, dataValue(data[newIndex], valueField));
            setSelectedItem(data[newIndex]);
            return;
          }
          else {
            newIndex = newIndex + 1;
          }
        }

        break;
      }
    }
  };

  const tabsClasses = {
    [vdlPrefix('tabs')]: true,
    [vdlPrefix('tabs--' + orientation)]: true
  };

  const tabsContainerClasses = {
    [vdlPrefix('tabs__container')]: true,
    [vdlPrefix('tabs__container--' + orientation)]: true
  };

  const tabToolbarClasses = {
    [vdlPrefix('tabs__toolbar')]: true,
    [vdlPrefix('tabs__toolbar--' + orientation)]: true
  };

  const toolbarContainerClasses = {
    [vdlPrefix('tabs__toolbar__container')]: true,
    [vdlPrefix('tabs__toolbar__container--' + orientation)]: true
  };

  const tabsContentClasses = {
    [vdlPrefix('tabs__content')]: true,
    [vdlPrefix('tabs__content--' + orientation)]: true
  };

  const tabs = !data.length ?
    (
      [<TabComponent
        key={'tab_0'}
        empty
        disabled={disabled}
        role="tab"
        aria-controls={tabContentId(id, 0)}
      >
        {result(messages.emptyTabs, props)}
      </TabComponent>]
    )
    : data.map(
      (item, idx) => {
        const currentId = optionId(id, idx);
        const isDisabled = isDisabledItem(item, props);

        return (
          <TabComponent
            key={'tab_' + idx}
            id={dataId(item, idField) || currentId}
            index={idx}
            dataItem={item}
            disabled={isDisabled}
            orientation={orientation}
            selected={contains(item, selectedItem, valueField)}
            onNavigate={onNavigate}
            onSelect={onChange}
            role="tab"
            aria-controls={tabContentId(id, idx)}
          >
            {
              ItemComponent ?
                (
                  <ItemComponent
                    item={item}
                    disabled={isDisabled}
                  />
                )
                : dataText(item, textField)
            }
          </TabComponent>
        );
      }
    );

  if (LastTabComponent && orientation === 'horizontal') {
    tabs.push(<LastTabComponent />);
  }

  const selectedIndex = dataIndexOf(data, selectedItem, valueField);

  return (
    <div className={classNames(tabsClasses)}>
      <div className={classNames(tabsContainerClasses)} >
        <div className={classNames(tabToolbarClasses)} >
          <ul className={classNames(toolbarContainerClasses)} role="tablist">
            {tabs}
          </ul>
        </div>
        <div className={classNames(tabsContentClasses)} role="tabpanel" tabIndex={disableTabPanelTabStop ? -1 : 0} id={tabContentId(id, selectedIndex)}>
          {children}
        </div>
      </div>
    </div>
  );
};

Tabs.displayName = 'Tabs';
